import { Command } from "commander";
import { readFileSync, existsSync } from "node:fs";
import { resolve } from "node:path";
import chalk from "chalk";
import { parseSpec } from "@fathom/core";
import { fatalError, warnAction } from "../ux/errors.js";

export const validateCommand = new Command("validate")
  .description("Validate a spec file format without running full analysis")
  .argument("<spec>", "Path to the spec markdown file")
  .action((specPath: string) => {
    const fullPath = resolve(specPath);

    if (!existsSync(fullPath)) {
      fatalError("Spec not found", fullPath + " does not exist", "Check the file path");
    }

    const content = readFileSync(fullPath, "utf-8");

    console.log(chalk.bold("\nFathom — Validate Spec"));
    console.log(chalk.dim("─".repeat(50)));
    console.log(chalk.dim(`File: ${fullPath}`));

    const issues: string[] = [];
    let spec;

    try {
      spec = parseSpec(content);
    } catch (e) {
      fatalError("Spec parse error", (e as Error).message, "Fix the spec syntax and try again");
    }

    // Check frontmatter
    if (!spec.metadata.project) {
      issues.push("Missing frontmatter `project` field");
    }

    // Check features
    if (spec.features.length === 0) {
      issues.push("No features found (use ### Feature: <name> headings)");
    }

    for (const f of spec.features) {
      if (!f.description || f.description.length < 10) {
        issues.push(`Feature "${f.name}": description is too short or missing`);
      }
      if (!f.category || f.category === "general") {
        issues.push(
          `Feature "${f.name}": no category specified (defaults to "general")`,
        );
      }
      if (f.aiTasks.length === 0) {
        issues.push(
          `Feature "${f.name}": no AI Tasks defined (estimation will use defaults)`,
        );
      }
    }

    // Report
    if (issues.length === 0) {
      console.log(
        chalk.green(
          `\n  ✓ Valid spec: ${spec.features.length} features, project "${spec.metadata.project ?? "unset"}"`,
        ),
      );
    } else {
      console.log(chalk.yellow(`\n  Found ${issues.length} issue(s):\n`));
      for (const issue of issues) {
        console.log(chalk.yellow(`    ⚠ ${issue}`));
      }
    }

    // Summary
    console.log(chalk.dim(`\n  Features: ${spec.features.length}`));
    const complexities = spec.features.reduce(
      (acc, f) => {
        acc[f.complexity] = (acc[f.complexity] ?? 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    );
    console.log(
      chalk.dim(
        `  Complexity: ${Object.entries(complexities)
          .map(([k, v]) => `${v}×${k}`)
          .join(", ")}`,
      ),
    );
    console.log();
  });
